import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@templates/index';
import { Button, Card, Input } from '@atoms/index';
import { apiClient } from '@services/api';

/**
 * Complete Registration Page
 * User completes registration by providing name, surname, and birth date
 */
const CompleteRegistrationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    birth_date: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if no email
  if (!email) {
    navigate('/register');
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Validate age (client-side check)
      const birthDate = new Date(formData.birth_date);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const actualAge =
        monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())
          ? age - 1
          : age;

      if (actualAge < 18) {
        setError('Kayıt olmak için 18 yaşında veya daha büyük olmalısınız.');
        setIsLoading(false);
        return;
      }

      await apiClient.post('/auth/complete-registration', {
        email,
        ...formData,
      });

      // Navigate to terms acceptance page
      navigate('/accept-terms', { state: { email, ...formData } });
    } catch (err: any) {
      console.error('Complete registration error:', err);
      setError(
        err.response?.data?.error?.message ||
          'Kayıt bilgileri kaydedilemedi. Lütfen tekrar deneyin.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate max date (18 years ago)
  const today = new Date();
  const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate())
    .toISOString()
    .split('T')[0];

  return (
    <MainLayout>
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-block p-4 rounded-3xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-premium mb-6">
              <svg
                className="w-12 h-12 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">Kayıt Bilgilerinizi Tamamlayın</h1>
            <p className="text-gray-600 text-lg">Birkaç bilgi daha ve başlayabilirsiniz</p>
          </div>

          {/* Card */}
          <Card padding="none" className="overflow-hidden backdrop-blur-xl animate-scale-in">
            <div className="p-8">
              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 rounded-xl bg-error/10 border border-error/30">
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 text-error flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-sm text-error font-medium">{error}</p>
                  </div>
                </div>
              )}

              {/* Registration Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Ad <span className="text-error">*</span>
                  </label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Adınız"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={isLoading}
                    required
                    minLength={2}
                    maxLength={50}
                    className="w-full"
                  />
                </div>

                {/* Surname */}
                <div>
                  <label htmlFor="surname" className="block text-sm font-medium text-gray-700 mb-2">
                    Soyad <span className="text-error">*</span>
                  </label>
                  <Input
                    id="surname"
                    type="text"
                    placeholder="Soyadınız"
                    value={formData.surname}
                    onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                    disabled={isLoading}
                    required
                    minLength={2}
                    maxLength={50}
                    className="w-full"
                  />
                </div>

                {/* Birth Date */}
                <div>
                  <label
                    htmlFor="birth_date"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Doğum Tarihi <span className="text-error">*</span>
                  </label>
                  <Input
                    id="birth_date"
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                    disabled={isLoading}
                    required
                    max={maxDate}
                    className="w-full"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Kayıt olmak için 18 yaşında veya daha büyük olmalısınız.
                  </p>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={
                    isLoading || !formData.name || !formData.surname || !formData.birth_date
                  }
                  isLoading={isLoading}
                >
                  Devam Et
                </Button>
              </form>

              {/* Back to Register */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => navigate('/register')}
                  className="text-sm text-gray-600 hover:text-primary font-medium transition-colors"
                >
                  ← Geri dön
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default CompleteRegistrationPage;
